import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { motion, AnimatePresence } from 'framer-motion';
import { Send } from 'lucide-react';

const Container = styled.div`
  display: flex;
  overflow: hidden;
  box-sizing: border-box;
  
  * {
    box-sizing: border-box;
  }
`;

const Controls = styled.div`
  flex: 1;
  padding: 24px;
  border-right: 1px solid #e5e7eb;
  overflow-y: auto;
  min-width: 0; // Prevent flex item from overflowing
`;

const Preview = styled.div`
  flex: 1;
  background: #f9fafb;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background-image: radial-gradient(#e5e7eb 1px, transparent 1px);
  background-size: 20px 20px;
  min-width: 0; // Prevent flex item from overflowing
`;

const SectionTitle = styled.h5`
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 12px;
`;

const ThemeGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 24px;
`;

const ThemeChip = styled.button<{ active?: boolean; color: string }>`
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  border: 1px solid ${props => props.active ? props.color : '#e5e7eb'};
  background: ${props => props.active ? `${props.color}15` : '#ffffff'};
  color: ${props => props.active ? props.color : '#4b5563'};
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    border-color: ${props => props.color};
  }
`;

const HeaderStyleOption = styled.div<{ active?: boolean }>`
  padding: 16px;
  border: 2px solid ${props => props.active ? '#7c3aed' : '#e5e7eb'};
  border-radius: 12px;
  margin-bottom: 12px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  .label {
    font-size: 13px;
    font-weight: 600;
    color: #111827;
  }
  
  .preview {
    width: 60%;
    height: 32px;
    background: linear-gradient(90deg, #1e293b, #334155);
    border-radius: 6px;
  }
`;

const Chatbot = styled(motion.div)`
  width: 320px;
  height: 500px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  overflow: hidden;
  border: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
`;

const ChatHeader = styled.div<{ bg: string }>`
  background: ${props => props.bg};
  padding: 16px;
  color: #ffffff;
  display: flex;
  align-items: center;
  gap: 12px;
  height: 70px;
`;

const ChatBody = styled.div`
  flex: 1;
  padding: 20px;
  background: #f9fafb;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ChatMsg = styled.div<{ isUser?: boolean; color?: string }>`
  padding: 12px 16px;
  border-radius: 12px;
  font-size: 13px;
  max-width: 80%;
  align-self: ${props => props.isUser ? 'flex-end' : 'flex-start'};
  background: ${props => props.isUser ? props.color : '#ffffff'};
  color: ${props => props.isUser ? '#ffffff' : '#1f2937'};
  border: ${props => props.isUser ? 'none' : '1px solid #e5e7eb'};
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
`;

const InputArea = styled.div`
  padding: 16px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  gap: 12px;
  background: #ffffff;
`;

const themes = [
  { id: 'purple', name: 'Purple Dreams', color: '#7c3aed', bg: 'linear-gradient(135deg, #7c3aed, #6d28d9)' },
  { id: 'dark', name: 'Executive Dark', color: '#1f2937', bg: '#1f2937' },
  { id: 'forest', name: 'Forest Canopy', color: '#059669', bg: 'linear-gradient(135deg, #059669, #047857)' },
  { id: 'monochrome', name: 'Monochrome', color: '#FFFFFF', bg: '#a93c3cff' },
];

const CustomizationVisual: React.FC = () => {
  const [activeTheme, setActiveTheme] = useState(themes[0]);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTheme(prev => {
        const currentIndex = themes.findIndex(t => t.id === prev.id);
        const nextIndex = (currentIndex + 1) % themes.length;
        return themes[nextIndex];
      });
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <Container>
      <Controls>
        <SectionTitle>Header Style</SectionTitle>
        <HeaderStyleOption active>
          <div className="label">Modern Gradient</div>
          <div className="preview" style={{ background: activeTheme.bg }} />
        </HeaderStyleOption>
        <HeaderStyleOption>
          <div className="label">Compact Minimal</div>
          <div className="preview" style={{ background: activeTheme.color }} />
        </HeaderStyleOption>

        <SectionTitle style={{ marginTop: 32 }}>Bot Icon</SectionTitle>
        <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
          <div style={{ width: 48, height: 48, borderRadius: '50%', background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #e5e7eb' }}>
            <img src="https://api.dicebear.com/7.x/avataaars/svg?seed=Felix" alt="Bot" style={{ width: 32, height: 32 }} />
          </div>
          <div style={{ flex: 1, border: '1px solid #e5e7eb', borderRadius: 8, padding: '0 12px', display: 'flex', alignItems: 'center', fontSize: 13, color: '#6b7280' }}>
            https://example.com/bot-icon.png
          </div>
        </div>

        <SectionTitle>Preset Themes</SectionTitle>
        <ThemeGrid>
          {themes.map(theme => (
            <ThemeChip
              key={theme.id}
              active={activeTheme.id === theme.id}
              color={theme.color}
              onClick={() => setActiveTheme(theme)}
            >
              {theme.name}
            </ThemeChip>
          ))}
        </ThemeGrid>
      </Controls>

      <Preview>
        <AnimatePresence mode="wait">
          <Chatbot
            key={activeTheme.id}
            initial={{ opacity: 0.8, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0.8, scale: 0.98 }}
            transition={{ duration: 0.2 }}
          >
            <ChatHeader bg={activeTheme.bg}>
              <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <img src="https://api.dicebear.com/7.x/avataaars/svg?seed=Felix" alt="Bot" style={{ width: 24, height: 24 }} />
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600 }}>Freddie Follicle</div>
                <div style={{ fontSize: 11, opacity: 0.9 }}>Your AI Hair Expert!</div>
              </div>
            </ChatHeader>
            <ChatBody>
              <ChatMsg>Hello! How can I help you?</ChatMsg>
              <ChatMsg isUser color={activeTheme.color}>I'd like to know more about Menoxidy</ChatMsg>
              <ChatMsg>Menoxidy is a product or treatment we currently offer. Feel free to ask about our available treatments!</ChatMsg>
            </ChatBody>
            <InputArea>
              <div style={{ flex: 1, color: '#9ca3af', fontSize: 13 }}>Type your message...</div>
              <Send size={16} color={activeTheme.color} />
            </InputArea>
          </Chatbot>
        </AnimatePresence>
      </Preview>
    </Container>
  );
};

export default CustomizationVisual;
